import React, { useState, useEffect } from 'react';
import api from '../../api';

const PRIORITY_COLOR = {
  urgent: { bg: '#fee2e2', text: '#991b1b' },
  high:   { bg: '#ffedd5', text: '#9a3412' },
  normal: { bg: '#f1f5f9', text: '#475569' },
  low:    { bg: '#f8fafc', text: '#94a3b8' },
};
const STATUS_COLOR = {
  open:        { bg: '#fef3c7', text: '#b45309' },
  in_progress: { bg: '#ede9fe', text: '#6d28d9' },
  completed:   { bg: '#dcfce7', text: '#166534' },
  cancelled:   { bg: '#f1f5f9', text: '#64748b' },
};

const CATEGORIES = ['General Maintenance','Plumbing','Electrical','HVAC','Cleaning','Landscaping',
  'Pool / Spa','Pest Control','Painting','Roofing','Appliance','Security','Structural','Other'];

const INPUT = { width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7,
  border: '1px solid #e2e8f0', background: '#f8fafc', color: '#1e293b', boxSizing: 'border-box' };

// ── Bids Panel Modal ──────────────────────────────────────────────────────────
function BidsPanel({ order, onAward, onClose }) {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [awarding, setAwarding] = useState(null);

  useEffect(() => {
    api.get(`/pm/work-orders/${order.id}/bids`)
      .then(r => setBids(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [order.id]);

  async function award(workerId) {
    setAwarding(workerId);
    try { await api.put(`/pm/work-orders/${order.id}/award/${workerId}`); onAward(); }
    catch {} finally { setAwarding(null); }
  }

  const stars = n => n ? '★'.repeat(n) + '☆'.repeat(5 - n) : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 460, padding: 24, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 4 }}>
          🙋 Who's Interested
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>{order.title}</div>

        {loading ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</div>
        ) : bids.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13, padding: '24px 0', textAlign: 'center' }}>
            No one has raised their hand yet.
          </div>
        ) : bids.map((bid, i) => (
          <div key={bid.id} style={{ display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 0', borderBottom: i < bids.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e0f2fe',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: '#0369a1', flexShrink: 0 }}>
              {i + 1}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{bid.name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {bid.phone && <span>{bid.phone}</span>}
                {bid.email && <span>{bid.phone ? ' · ' : ''}{bid.email}</span>}
              </div>
              {bid.internal_rating && (
                <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>
                  {stars(bid.internal_rating)}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0, textAlign: 'right' }}>
              {bid.status === 'awarded' ? (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 4,
                  background: '#dcfce7', color: '#166534' }}>Awarded ✓</span>
              ) : bid.status === 'declined' ? (
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Declined</span>
              ) : (
                <button onClick={() => award(bid.service_worker_id)}
                  disabled={awarding === bid.service_worker_id}
                  className="btn btn-primary btn-sm" style={{ fontSize: 12 }}>
                  {awarding === bid.service_worker_id ? 'Awarding…' : 'Award Job'}
                </button>
              )}
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                {new Date(bid.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}

        <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Work Order Modal ──────────────────────────────────────────────────────────
function WorkOrderModal({ order, properties, serviceWorkers, onSave, onClose }) {
  const isEdit = !!order?.id;
  const [form, setForm] = useState(order || {
    title: '', description: '', category: '', priority: 'normal',
    property_id: '', service_worker_id: '', bid_mode: false,
    due_date: '', estimated_cost: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // assignment mode: 'worker' | 'bid' | 'unassigned'
  const assignMode = form.bid_mode ? 'bid' : form.service_worker_id ? 'worker' : 'unassigned';

  // filter to workers with matching specialty (untagged workers always show)
  const eligibleWorkers = serviceWorkers.filter(w =>
    !form.category || !(w.specialties?.length) || w.specialties.includes(form.category)
  );

  function handleAssign(e) {
    const v = e.target.value;
    if (v === '__bid__') {
      setForm(p => ({ ...p, bid_mode: true, service_worker_id: '' }));
    } else if (v === '') {
      setForm(p => ({ ...p, bid_mode: false, service_worker_id: '' }));
    } else {
      setForm(p => ({ ...p, bid_mode: false, service_worker_id: v }));
    }
  }

  const assignValue = form.bid_mode ? '__bid__' : (form.service_worker_id || '');

  async function handleSave() {
    if (!form.title) return;
    setSaving(true);
    try {
      if (isEdit) await api.put(`/pm/work-orders/${order.id}`, form);
      else await api.post('/pm/work-orders', form);
      onSave();
    } catch {} finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 500, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, color: '#1e293b' }}>
          {isEdit ? 'Edit Work Order' : 'New Work Order'}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Title *</label>
          <input value={form.title} onChange={set('title')} placeholder="e.g. Replace kitchen faucet" style={INPUT} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Property</label>
            <select value={form.property_id} onChange={set('property_id')} style={INPUT}>
              <option value="">— all —</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Category</label>
            <select value={form.category} onChange={set('category')} style={INPUT}>
              <option value="">— select —</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Priority</label>
            <select value={form.priority} onChange={set('priority')} style={INPUT}>
              {['urgent','high','normal','low'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          {isEdit && (
            <div>
              <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Status</label>
              <select value={form.status} onChange={set('status')} style={INPUT}>
                {['open','in_progress','completed','cancelled'].map(v => <option key={v} value={v}>{v.replace('_',' ')}</option>)}
              </select>
            </div>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Assignment</label>
          <select value={assignValue} onChange={handleAssign} style={INPUT}>
            <option value="">— unassigned —</option>
            <option value="__bid__">🙋 Put out to bid</option>
            {eligibleWorkers.length > 0 && <option disabled>──────────────</option>}
            {eligibleWorkers.map(w => (
              <option key={w.id} value={w.id}>{w.name}{w.specialties?.length ? ` · ${w.specialties.join(', ')}` : ''}</option>
            ))}
          </select>
          {form.bid_mode && (
            <div style={{ fontSize: 12, color: '#6d28d9', marginTop: 6, padding: '6px 10px',
              background: '#ede9fe', borderRadius: 6 }}>
              This job will be visible to service workers — they can raise their hand and you award it.
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Due Date</label>
            <input type="date" value={form.due_date || ''} onChange={set('due_date')} style={INPUT} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Est. Cost ($)</label>
            <input type="number" value={form.estimated_cost || ''} onChange={set('estimated_cost')} style={INPUT} />
          </div>
        </div>

        {isEdit && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Actual Cost ($)</label>
            <input type="number" value={form.actual_cost || ''} onChange={set('actual_cost')} style={INPUT} />
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Description / Notes</label>
          <textarea value={form.description || ''} rows={3} onChange={set('description')}
            style={{ ...INPUT, resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.title}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Assignment cell ───────────────────────────────────────────────────────────
function AssignmentCell({ order, onBidsClick }) {
  if (order.bid_mode) {
    return (
      <button onClick={e => { e.stopPropagation(); onBidsClick(order); }}
        style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 5,
          background: '#ede9fe', color: '#6d28d9', border: 'none', cursor: 'pointer',
          whiteSpace: 'nowrap' }}>
        🙋 {order.bid_count > 0 ? `${order.bid_count} interested` : 'Open bid'}
      </button>
    );
  }
  if (order.service_worker_name) {
    return <span style={{ fontSize: 12, color: '#1e293b' }}>{order.service_worker_name}</span>;
  }
  return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
}

// ── Main View ─────────────────────────────────────────────────────────────────
export default function WorkOrdersView() {
  const [orders, setOrders] = useState([]);
  const [properties, setProperties] = useState([]);
  const [serviceWorkers, setServiceWorkers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('open');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [bidsFor, setBidsFor] = useState(null);

  function load() {
    const q = filterStatus ? `?status=${filterStatus}` : '';
    Promise.all([
      api.get(`/pm/work-orders${q}`),
      api.get('/pm/properties'),
      api.get('/pm/service-workers'),
    ]).then(([w, p, sw]) => {
      setOrders(w.data); setProperties(p.data); setServiceWorkers(sw.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [filterStatus]);

  return (
    <>
      <div className="page-header">
        <span className="page-title">Work Orders</span>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>+ New Work Order</button>
      </div>
      <div className="page-body">
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[['open','Open'],['in_progress','In Progress'],['completed','Completed'],['','All']].map(([v, l]) => (
            <button key={v} onClick={() => setFilterStatus(v)}
              className={`btn btn-sm ${filterStatus === v ? 'btn-primary' : 'btn-ghost'}`}>{l}</button>
          ))}
        </div>

        {loading ? <div className="loading">Loading…</div> : orders.length === 0 ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
            No work orders{filterStatus ? ` with status "${filterStatus.replace('_',' ')}"` : ''}.
          </div></div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Property</th>
                    <th>Category</th>
                    <th>Assignment</th>
                    <th>Due</th>
                    <th>Est. $</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(w => (
                    <tr key={w.id}>
                      <td style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{w.title}</td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        {w.property_name || '—'}{w.unit_number ? ` · ${w.unit_number}` : ''}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>{w.category || '—'}</td>
                      <td><AssignmentCell order={w} onBidsClick={setBidsFor} /></td>
                      <td style={{ fontSize: 12, color: w.due_date && new Date(w.due_date) < new Date() && w.status !== 'completed' ? '#dc2626' : '#94a3b8' }}>
                        {w.due_date ? new Date(w.due_date + 'T12:00').toLocaleDateString('en', { month: 'short', day: 'numeric' }) : '—'}
                      </td>
                      <td style={{ fontSize: 12 }}>{w.estimated_cost != null ? `$${Number(w.estimated_cost).toFixed(0)}` : '—'}</td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                          ...(PRIORITY_COLOR[w.priority] || PRIORITY_COLOR.normal) }}>{w.priority}</span>
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                          ...(STATUS_COLOR[w.status] || STATUS_COLOR.open) }}>{w.status.replace('_',' ')}</span>
                      </td>
                      <td>
                        <button onClick={() => setModal(w)}
                          style={{ fontSize: 12, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer' }}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {modal !== null && (
        <WorkOrderModal
          order={Object.keys(modal).length ? modal : null}
          properties={properties}
          serviceWorkers={serviceWorkers}
          onSave={() => { setModal(null); load(); }}
          onClose={() => setModal(null)} />
      )}
      {bidsFor && (
        <BidsPanel
          order={bidsFor}
          onAward={() => { setBidsFor(null); load(); }}
          onClose={() => setBidsFor(null)} />
      )}
    </>
  );
}
